import { NotificationTypeEnum } from "@ch/helpers/utils/enums";
import { onForegroundMessage } from "@ch/helpers/utils/firebase/firebase";
import trackPushNotification from "@ch/helpers/utils/firebase/trackPushNotification";
import type { NotificationData } from "@ch/helpers/utils/firebase/types";
import { AppRoutesEnum } from "@ch/router/AppRoutes/enums";
import type { MessagePayload } from "firebase/messaging";
import { useEffect } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useNavigate } from "react-router-dom";
// TODO: Uncomment these imports when implementing the notification routes
// import { ChatRoutesEnum } from "@ch/router/ChatRoutes/enums";
// import { AppointmentsRoutesEnum } from "@ch/router/ Appointments/enums";
// import { ForumsRoutesEnum } from "@ch/router/Forums/enums";
// import { MenuRoutesEnum } from "@ch/router/MenuRoutes/enums";
// import { SubscriptionRoutesEnum } from "@ch/router/SubscriptionRoutes/enums";
// import { HealthDiaryRoutesEnum } from "@ch/router/HealthDiaryRoutes/enums";

const handleNotificationClick = (
  data: NotificationData | undefined,
  navigate: NavigateFunction,
) => {
  const notificationType = data?.notificationType ?? "";
  const notificationId = data?.notificationId;

  // Track notification click
  if (notificationId) {
    void trackPushNotification({
      id: notificationId,
      isClicked: true,
    });
  }

  // Navigate based on notification type
  if (notificationType === String(NotificationTypeEnum.KEM)) {
    void navigate(AppRoutesEnum.KEM);
  }

  // TODO: Uncomment and implement these notification routes when ready
  // else if (notificationType === String(NotificationTypeEnum.CHAT)) {
  //   // Navigate to chat - may need chat room ID from payload
  //   void navigate(ChatRoutesEnum.ROOT);
  // } else if (
  //   notificationType === String(NotificationTypeEnum.REMINDER) ||
  //   notificationType === String(NotificationTypeEnum.COACH_APPOINTMENT)
  // ) {
  //   void navigate(AppRoutesEnum.APPOINTMENTS);
  // } else if (notificationType === String(NotificationTypeEnum.NEW_FORUM)) {
  //   void navigate(ForumsRoutesEnum.ROOT);
  // } else if (notificationType === String(NotificationTypeEnum.NEW_BLOG_POST)) {
  //   // Navigate to blog - may need blog post ID from payload
  //   void navigate(MenuRoutesEnum.ROOT);
  // } else if (
  //   notificationType === String(NotificationTypeEnum.NEW_SUBSCRIPTION) ||
  //   notificationType === String(NotificationTypeEnum.SUBSCRIPTION_RENEWAL)
  // ) {
  //   void navigate(SubscriptionRoutesEnum.ROOT);
  // } else if (notificationType === String(NotificationTypeEnum.HEALTH_DIARY)) {
  //   void navigate(HealthDiaryRoutesEnum.ROOT);
  // } else {
  //   // Default: Navigate to notification details or notifications page
  //   const id = notificationId;
  //   if (id) {
  //     void navigate(`${AppRoutesEnum.NOTIFICATIONS}/${id}`);
  //   } else {
  //     void navigate(AppRoutesEnum.NOTIFICATIONS);
  //   }
  // }
};

const useNotificationListeners = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onForegroundMessage(async (payload: MessagePayload) => {
      const data = payload?.data as NotificationData | undefined;

      const title = data?.title;
      const body = data?.body;

      try {
        if ("Notification" in window && Notification.permission === "granted") {
          const notification = new Notification(title ?? "mDoc", {
            body,
          });

          notification.onclick = () => {
            notification.close();
            handleNotificationClick(data, navigate);
          };
        }
        // Track notification delivery
        const notificationId = data?.notificationId;
        if (notificationId) {
          void trackPushNotification({
            id: notificationId,
            isDelivered: true,
          });
        }
      } catch {
        // Notification creation failed, silently handle
      }
    });

    // listen for NOTIFICATION_CLICK messages from the service worker from firebase-messaging-sw.js to handle notification clicks routing
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      const messageData = event.data as
        | {
            type: "NOTIFICATION_CLICK";
            data?: NotificationData;
          }
        | undefined;
      if (messageData?.type === "NOTIFICATION_CLICK") {
        handleNotificationClick(messageData.data, navigate);
      }
    };

    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener(
        "message",
        handleServiceWorkerMessage,
      );
    }

    return () => {
      unsubscribe?.();
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.removeEventListener(
          "message",
          handleServiceWorkerMessage,
        );
      }
    };
  }, [navigate]);
};

export default useNotificationListeners;
